import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, ArrowRight, MapPinned, Shield, Clock, Globe } from 'lucide-react';
import useAuthStore from '../store/authStore';

/* ─── PALETTE (cohérente avec la landing) ─── */
const C = {
  sand:      '#f5ede0',
  sandDark:  '#e8d8c0',
  terra:     '#c2521a',
  terraLt:   '#e0784a',
  terraDk:   '#8a3510',
  forest:    '#1a3d28',
  forestMid: '#244d35',
  forestLt:  '#2f6645',
  gold:      '#c8902a',
  goldLt:    '#e8b84a',
  ink:       '#0e1a10',
  mist:      '#6e8070',
  cream:     '#faf6ef',
};

/* ─── CSS ─── */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600&family=DM+Sans:wght@300;400;500;600;700&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html, body, #root { height: 100%; }

body {
  font-family: 'DM Sans', sans-serif;
  background: ${C.cream};
  color: ${C.forest};
  overflow: hidden;
}

/* ── LAYOUT ── */
.login-shell {
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 100vh;
}
@media (max-width: 900px) {
  .login-shell { grid-template-columns: 1fr; }
  .login-left  { display: none; }
}

/* ── LEFT PANEL ── */
.login-left {
  position: relative;
  background: ${C.forest};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 44px 48px;
}
.left-pattern {
  position: absolute;
  inset: 0;
  background-image:
    repeating-linear-gradient(0deg, rgba(245,237,224,.028) 0, rgba(245,237,224,.028) 1px, transparent 1px, transparent 56px),
    repeating-linear-gradient(90deg, rgba(245,237,224,.028) 0, rgba(245,237,224,.028) 1px, transparent 1px, transparent 56px);
  pointer-events: none;
}
.left-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(70px);
  pointer-events: none;
}

/* logo */
.left-logo {
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
  z-index: 2;
  text-decoration: none;
}
.left-logo-mark {
  width: 44px; height: 44px;
  border-radius: 13px;
  background: rgba(245,237,224,.1);
  border: 1px solid rgba(245,237,224,.15);
  display: grid; place-items: center;
}
.left-logo-text {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${C.sand};
  letter-spacing: -.02em;
}
.left-logo-text em { color: ${C.terraLt}; font-style: normal; }

/* center content */
.left-center {
  position: relative;
  z-index: 2;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0;
}
.left-map-zone {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  max-width: 340px;
  margin: 0 auto 36px;
}
.map-bg-circle {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: radial-gradient(circle at 40% 45%, rgba(47,102,69,.55), rgba(14,26,16,.9));
  border: 1px solid rgba(245,237,224,.07);
  overflow: hidden;
}
.map-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(245,237,224,.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(245,237,224,.05) 1px, transparent 1px);
  background-size: 32px 32px;
  border-radius: 50%;
  mask-image: radial-gradient(circle, black 60%, transparent 100%);
}
.map-africa {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 7.5rem;
  filter: drop-shadow(0 0 40px rgba(194,82,26,.45));
  animation: float 5s ease-in-out infinite;
}
@keyframes float {
  0%,100% { transform: translateY(0) scale(1); }
  50%      { transform: translateY(-8px) scale(1.02); }
}

/* orbit rings */
.orbit-ring {
  position: absolute;
  inset: -8%;
  border-radius: 50%;
  border: 1px dashed rgba(200,144,42,.2);
  animation: spin-slow 24s linear infinite;
}
.orbit-ring.r2 {
  inset: -18%;
  border-color: rgba(194,82,26,.14);
  animation-duration: 36s;
  animation-direction: reverse;
}
@keyframes spin-slow { to { transform: rotate(360deg); } }

/* orbit dots */
.orbit-dot {
  position: absolute;
  width: 10px; height: 10px;
  border-radius: 50%;
  background: ${C.terra};
  box-shadow: 0 0 0 3px rgba(194,82,26,.25);
  top: 4%; left: 50%;
  transform: translateX(-50%);
}
.orbit-dot.d2 {
  width: 7px; height: 7px;
  background: ${C.gold};
  box-shadow: 0 0 0 3px rgba(200,144,42,.2);
  top: auto; bottom: 6%; left: 30%;
}

/* pin markers */
.map-pin {
  position: absolute;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 4;
}
.pin-dot {
  width: 11px; height: 11px;
  border-radius: 50%;
  background: ${C.terraLt};
  position: relative;
  flex: none;
}
.pin-dot::after {
  content: '';
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  border: 1.5px solid ${C.terraLt};
  opacity: .35;
  animation: ripple-pin 2.2s infinite;
}
@keyframes ripple-pin {
  0%   { transform: scale(.6); opacity: .6; }
  100% { transform: scale(2);  opacity: 0; }
}
.pin-chip {
  background: rgba(14,26,16,.75);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(245,237,224,.12);
  border-radius: 8px;
  padding: 5px 10px;
  font-size: .72rem;
  color: ${C.sand};
  white-space: nowrap;
  font-weight: 500;
}

/* left headline */
.left-headline {
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(1.9rem, 3.2vw, 2.7rem);
  font-weight: 600;
  line-height: 1.08;
  color: ${C.sand};
  letter-spacing: -.03em;
  text-align: center;
  max-width: 360px;
  margin: 0 auto;
}
.left-headline em { color: ${C.goldLt}; font-style: italic; }

/* stats strip */
.left-stats {
  display: flex;
  justify-content: center;
  gap: 32px;
  margin-top: 28px;
  position: relative;
  z-index: 2;
}
.left-stat {
  text-align: center;
}
.left-stat-num {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.8rem;
  font-weight: 700;
  color: ${C.goldLt};
  line-height: 1;
}
.left-stat-lbl {
  font-size: .72rem;
  color: rgba(245,237,224,.45);
  margin-top: 3px;
}

/* bottom badges */
.left-badges {
  display: flex;
  gap: 10px;
  position: relative;
  z-index: 2;
  flex-wrap: wrap;
}
.left-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(245,237,224,.06);
  border: 1px solid rgba(245,237,224,.1);
  border-radius: 10px;
  padding: 8px 14px;
  font-size: .78rem;
  color: rgba(245,237,224,.65);
}
.left-badge svg { color: ${C.terraLt}; flex: none; }

/* ── RIGHT PANEL ── */
.login-right {
  background: ${C.cream};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 32px;
  position: relative;
  overflow: hidden;
}
.right-bg-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
}

.login-form-wrap {
  width: 100%;
  max-width: 420px;
  position: relative;
  z-index: 2;
}

/* mobile logo */
.mobile-logo {
  display: none;
  align-items: center;
  gap: 12px;
  margin-bottom: 36px;
  text-decoration: none;
}
.mobile-logo-mark {
  width: 40px; height: 40px;
  border-radius: 12px;
  background: ${C.forest};
  display: grid; place-items: center;
}
.mobile-logo-text {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.4rem;
  font-weight: 700;
  color: ${C.forest};
}
.mobile-logo-text em { color: ${C.terra}; font-style: normal; }
@media (max-width: 900px) {
  .mobile-logo { display: flex; }
}

/* form header */
.form-eyebrow {
  font-size: .72rem;
  font-weight: 700;
  letter-spacing: .18em;
  text-transform: uppercase;
  color: ${C.terra};
  margin-bottom: 10px;
}
.form-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(2rem, 4vw, 2.8rem);
  font-weight: 700;
  line-height: 1.02;
  letter-spacing: -.04em;
  color: ${C.ink};
  margin-bottom: 8px;
}
.form-title em { color: ${C.terra}; font-style: italic; }
.form-sub {
  color: ${C.mist};
  font-size: .9rem;
  line-height: 1.65;
  margin-bottom: 36px;
}

/* error */
.form-error {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(194,82,26,.08);
  border: 1px solid rgba(194,82,26,.22);
  color: ${C.terraDk};
  border-radius: 12px;
  padding: 13px 16px;
  font-size: .88rem;
  margin-bottom: 22px;
}
.form-error-dot {
  width: 7px; height: 7px;
  border-radius: 50%;
  background: ${C.terra};
  flex: none;
}

/* fields */
.field-group {
  display: grid;
  gap: 18px;
  margin-bottom: 8px;
}
.field-wrap {
  display: grid;
  gap: 7px;
}
.field-label {
  font-size: .72rem;
  font-weight: 700;
  letter-spacing: .12em;
  text-transform: uppercase;
  color: ${C.mist};
}
.field-input-wrap {
  position: relative;
}
.field-input {
  width: 100%;
  border: 1.5px solid rgba(26,61,40,.14);
  border-radius: 13px;
  background: white;
  color: ${C.ink};
  padding: 14px 18px;
  font: 500 .97rem 'DM Sans', sans-serif;
  outline: none;
  transition: border-color .2s, box-shadow .2s;
  appearance: none;
}
.field-input:focus {
  border-color: ${C.terra};
  box-shadow: 0 0 0 4px rgba(194,82,26,.1);
}
.field-input.has-icon { padding-right: 50px; }
.field-input::placeholder { color: rgba(110,128,112,.45); }
.field-icon-btn {
  position: absolute;
  right: 14px;
  top: 50%; transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${C.mist};
  display: flex; align-items: center;
  padding: 4px;
  border-radius: 6px;
  transition: color .2s;
}
.field-icon-btn:hover { color: ${C.forest}; }

/* forgot */
.forgot-row {
  display: flex;
  justify-content: flex-end;
  margin-top: -6px;
  margin-bottom: 28px;
}
.forgot-link {
  font-size: .82rem;
  color: ${C.mist};
  text-decoration: none;
  transition: color .2s;
}
.forgot-link:hover { color: ${C.terra}; }

/* submit */
.btn-submit {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 28px;
  border-radius: 14px;
  background: ${C.forest};
  color: ${C.sand};
  font: 700 .97rem 'DM Sans', sans-serif;
  border: none;
  cursor: pointer;
  transition: transform .25s, box-shadow .25s, background .2s;
  box-shadow: 0 12px 32px rgba(26,61,40,.2);
  position: relative;
  overflow: hidden;
}
.btn-submit::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.06), transparent);
  transform: translateX(-100%);
  transition: transform .5s ease;
}
.btn-submit:hover::before { transform: translateX(100%); }
.btn-submit:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 18px 42px rgba(26,61,40,.28);
  background: ${C.forestMid};
}
.btn-submit:disabled { opacity: .65; cursor: not-allowed; transform: none; }

/* spinner */
.spinner {
  width: 20px; height: 20px;
  border: 2.5px solid rgba(245,237,224,.3);
  border-top-color: ${C.sand};
  border-radius: 50%;
  animation: spin .8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* divider */
.divider {
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 24px 0;
}
.divider-line { flex: 1; height: 1px; background: rgba(26,61,40,.08); }
.divider-text { font-size: .78rem; color: ${C.mist}; white-space: nowrap; }

/* footer note */
.form-footer {
  margin-top: 28px;
  text-align: center;
  font-size: .82rem;
  color: ${C.mist};
}
.form-footer a {
  color: ${C.terra};
  font-weight: 600;
  text-decoration: none;
  transition: color .2s;
}
.form-footer a:hover { color: ${C.terraDk}; }

/* trust strip */
.trust-strip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-top: 28px;
  flex-wrap: wrap;
}
.trust-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: .76rem;
  color: rgba(110,128,112,.7);
}
.trust-item svg { color: ${C.terra}; }
`;

/* ─── ANIMATED CANVAS ─── */
function LeftCanvas() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W = canvas.width = canvas.offsetWidth;
    let H = canvas.height = canvas.offsetHeight;
    const pts = Array.from({ length: 18 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      vx: (Math.random() - .5) * .15, vy: (Math.random() - .5) * .15,
      r: Math.random() * 1.8 + .4,
    }));
    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      pts.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(245,237,224,.18)';
        ctx.fill();
      });
      pts.forEach((p, i) => pts.slice(i + 1).forEach(q => {
        const d = Math.hypot(p.x - q.x, p.y - q.y);
        if (d < 140) {
          ctx.beginPath();
          ctx.moveTo(p.x, p.y); ctx.lineTo(q.x, q.y);
          ctx.strokeStyle = `rgba(245,237,224,${.04 * (1 - d / 140)})`;
          ctx.lineWidth = .5; ctx.stroke();
        }
      }));
      raf = requestAnimationFrame(draw);
    };
    draw();
    const resize = () => { W = canvas.width = canvas.offsetWidth; H = canvas.height = canvas.offsetHeight; };
    window.addEventListener('resize', resize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position:'absolute', inset:0, width:'100%', height:'100%', pointerEvents:'none', zIndex:1 }} />;
}

/* ─── MAIN ─── */
export default function Login() {
  const navigate = useNavigate();
  const { login, loading, error } = useAuthStore();
  const [form, setForm]       = useState({ username: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [focused, setFocused] = useState('');
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ok = await login(form);
    if (ok) navigate('/dashboard');

  };

  const STAGGER = {
    hidden: {},
    visible: { transition: { staggerChildren: .08 } },
  };
  const ITEM = {
    hidden:  { opacity: 0, y: 18 },
    visible: { opacity: 1, y: 0, transition: { duration: .55, ease: [.22, 1, .36, 1] } },
  };

  return (
    <div className="login-shell">
      <style>{css}</style>

      {/* ── LEFT ── */}
      <div className="login-left">
        <LeftCanvas />
        <div className="left-pattern" />
        <div className="left-orb" style={{ width:400, height:400, top:-100, right:-100, background:'radial-gradient(circle, rgba(47,102,69,.4), transparent 70%)' }} />
        <div className="left-orb" style={{ width:300, height:300, bottom:-80, left:-60, background:'radial-gradient(circle, rgba(194,82,26,.2), transparent 70%)' }} />

        {/* logo */}
        <motion.a href="/" className="left-logo"
          initial={{ opacity:0, x:-16 }} animate={{ opacity:1, x:0 }} transition={{ duration:.6, ease:[.22,1,.36,1] }}>
          <div className="left-logo-mark">
            <MapPinned size={20} color="rgba(245,237,224,.8)" />
          </div>
          <div className="left-logo-text">Terra<em>Track</em></div>
        </motion.a>

        {/* center */}
        <div className="left-center">
          {/* globe visual */}
          <motion.div className="left-map-zone"
            initial={{ opacity:0, scale:.88 }} animate={{ opacity:1, scale:1 }}
            transition={{ duration:.9, delay:.2, ease:[.22,1,.36,1] }}>
            <div className="map-bg-circle">
              <div className="map-grid" />
            </div>
            <div className="orbit-ring">
              <div className="orbit-dot" />
            </div>
            <div className="orbit-ring r2">
              <div className="orbit-dot d2" />
            </div>
            <div className="map-africa">🌍</div>

            {/* pins */}
            {[
              { top:'26%', left:'38%', label:'Lomé', delay:.8 },
              { top:'46%', left:'16%', label:'Dakar', delay:1.0 },
              { top:'52%', left:'58%', label:'Cotonou', delay:1.2 },
            ].map(({ top, left, label, delay }) => (
              <motion.div key={label} className="map-pin" style={{ top, left }}
                initial={{ opacity:0, scale:0 }} animate={{ opacity:1, scale:1 }}
                transition={{ delay, duration:.45, ease:[.22,1,.36,1] }}>
                <div className="pin-dot" />
                <div className="pin-chip">{label}</div>
              </motion.div>
            ))}
          </motion.div>

          {/* headline */}
          <motion.h2 className="left-headline"
            initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }}
            transition={{ delay:.4, duration:.7, ease:[.22,1,.36,1] }}>
            Achetez en Afrique<br />
            <em>sans risque foncier</em>
          </motion.h2>

          {/* stats */}
          <motion.div className="left-stats"
            initial={{ opacity:0 }} animate={{ opacity:1 }}
            transition={{ delay:.6, duration:.6 }}>
            {[['2 400+','Dossiers'],['98%','Fiabilité'],['72h','Délai'],['12','Pays']].map(([num, lbl]) => (
              <div key={lbl} className="left-stat">
                <div className="left-stat-num">{num}</div>
                <div className="left-stat-lbl">{lbl}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* bottom badges */}
        <motion.div className="left-badges"
          initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }}
          transition={{ delay:.7, duration:.6 }}>
          <div className="left-badge"><Shield size={13} /> Données chiffrées</div>
          <div className="left-badge"><Clock size={13} /> Réponse 72h</div>
          <div className="left-badge"><Globe size={13} /> 12 pays</div>
        </motion.div>
      </div>

      {/* ── RIGHT ── */}
      <div className="login-right">
        <div className="right-bg-blob" style={{ width:500, height:500, top:-150, right:-200, background:`radial-gradient(circle, rgba(194,82,26,.06), transparent 70%)` }} />
        <div className="right-bg-blob" style={{ width:350, height:350, bottom:-100, left:-80, background:`radial-gradient(circle, rgba(47,102,69,.05), transparent 70%)` }} />

        <div className="login-form-wrap">
          {/* mobile logo */}
          <a href="/" className="mobile-logo">
            <div className="mobile-logo-mark"><MapPinned size={18} color="rgba(245,237,224,.9)" /></div>
            <div className="mobile-logo-text">Terra<em>Track</em></div>
          </a>

          <motion.div variants={STAGGER} initial="hidden" animate="visible">
            <motion.div variants={ITEM} className="form-eyebrow">Espace client</motion.div>
            <motion.h1 variants={ITEM} className="form-title">
              Bienvenue<br /><em>de retour</em>
            </motion.h1>
            <motion.p variants={ITEM} className="form-sub">
              Connectez-vous pour accéder à vos dossiers de vérification et rapports d'expertise.
            </motion.p>

            {/* error */}
            <AnimatePresence>
              {error && (
                <motion.div className="form-error"
                  initial={{ opacity:0, y:-10, height:0 }}
                  animate={{ opacity:1, y:0, height:'auto' }}
                  exit={{ opacity:0, height:0 }}
                  transition={{ duration:.3 }}>
                  <div className="form-error-dot" />
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            <motion.form variants={STAGGER} onSubmit={handleSubmit}>
              <div className="field-group">
                {/* username */}
                <motion.div variants={ITEM} className="field-wrap">
                  <label className="field-label">Identifiant</label>
                  <div className="field-input-wrap">
                    <input
                      className="field-input"
                      name="username"
                      value={form.username}
                      onChange={handleChange}
                      onFocus={() => setFocused('username')}
                      onBlur={() => setFocused(null)}
                      placeholder="Votre identifiant"
                      autoComplete="username"
                      required
                    />
                  </div>
                </motion.div>

                {/* password */}
                <motion.div variants={ITEM} className="field-wrap">
                  <label className="field-label">Mot de passe</label>
                  <div className="field-input-wrap">
                    <input
                      className={`field-input has-icon`}
                      name="password"
                      value={form.password}
                      onChange={handleChange}
                      onFocus={() => setFocused('password')}
                      onBlur={() => setFocused(null)}
                      type={showPass ? 'text' : 'password'}
                      placeholder="••••••••"
                      autoComplete="current-password"
                      required
                    />
                    <button type="button" className="field-icon-btn" onClick={() => setShowPass(v => !v)} tabIndex={-1}>
                      {showPass ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                </motion.div>
              </div>

              {/* forgot */}
              <motion.div variants={ITEM} className="forgot-row" style={{ marginTop:10 }}>
                <a href="/forgot-password" className="forgot-link">Mot de passe oublié ?</a>
              </motion.div>

              {/* submit */}
              <motion.button
                variants={ITEM}
                type="submit"
                className="btn-submit"
                disabled={loading}
                whileTap={{ scale: .98 }}
              >
                {loading ? (
                  <div className="spinner" />
                ) : (
                  <>Se connecter <ArrowRight size={17} /></>
                )}
              </motion.button>
            </motion.form>

            {/* trust */}
            <motion.div variants={ITEM} className="trust-strip">
              <div className="trust-item"><Shield size={12} /> Connexion sécurisée</div>
              <div className="trust-item"><Globe size={12} /> Données protégées</div>
            </motion.div>

            <motion.div variants={ITEM} className="form-footer">
              Pas encore de compte ?{' '}
              <a href="/register">Demander un accès</a>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}